import Asset, {AssetType} from "../../model/Asset"
import AssetConversion, {AssetConversionType} from "../../model/AssetConversion"
import type {IAssetDetail, IAssetDetailGroup, IFutureYear} from "./Calculator"
import {roundMoney} from "../../stores/StoreUtilities"
import {riderIncomes} from "./AnnuityRider"

export interface IIraRmdSummary {
  ownerId: string
  required: number
  distributed: number
  remaining: number
}

// Only an owner who holds a qualified rider contract with its linked income pools IRA RMDs. Everyone else,
// including that owner's spouse, keeps the per-account RMD treatment.
export function hasQualifiedRider(asset: Asset): boolean {
  const model = asset.model
  return !!model && model.assets.some(a => a.assetType === AssetType.QualifiedAnnuityWithIncomeRider && a.ownerId === asset.ownerId &&
    !a.archived && !a.netWorthExcluded && riderIncomes(model, a).length > 0)
}

// isIRA also identifies employer plans and inherited accounts in this application.
export function isAggregatableIra(asset: Asset): boolean {
  return !!asset.ownerId && !!asset.owner && asset.owner.id === asset.ownerId && !asset.owner.isJoint &&
    [AssetType.TraditionalIRA, AssetType.SEPIRA, AssetType.SimpleIRA,
      AssetType.QualifiedAnnuityWithIncomeRider].includes(asset.assetType) && hasQualifiedRider(asset)
}

function qcdCredit(ad: IAssetDetail, conversions: AssetConversion[]): number {
  return conversions.filter(c => c.conversionType === AssetConversionType.QCD && c.srcAssetId === ad.liquidAsset.id)
    .reduce((total, c) => total + c.amount, 0)
}

// Distribution excludes conversions and rider cash. rmd includes any reclassified YTD cash.
function accountCredits(ad: IAssetDetail, conversions: AssetConversion[]): number {
  return (ad.riderIncome ?? 0) - ad.distribution + ad.rmd + qcdCredit(ad, conversions)
}

function ownShortfall(ad: IAssetDetail, conversions: AssetConversion[]): number {
  return Math.max(0, (ad.grossRmd ?? 0) - accountCredits(ad, conversions))
}

function distributeRmd(year: IFutureYear, ad: IAssetDetail, amount: number) {
  ad.balance -= amount
  ad.rmd += amount
  ad.remainingRMD += amount
  year.rmds += amount
  if (year.yearNum === 1 && year.proRateBudget) year.remainingRMDs += amount
  year.allDistributions += amount
  year.iraDistributions += amount
  year.taxableIraDistributions += amount
}

export function iraRmdSummaries(details: IAssetDetail[], conversions: AssetConversion[]): IIraRmdSummary[] {
  const summaries = new Map<string, IIraRmdSummary>()
  details.filter(ad => isAggregatableIra(ad.liquidAsset)).forEach(ad => {
    const ownerId = ad.liquidAsset.ownerId
    const summary = summaries.get(ownerId) ?? {ownerId, required: 0, distributed: 0, remaining: 0}
    summary.required += ad.grossRmd ?? 0
    summary.distributed += accountCredits(ad, conversions)
    summaries.set(ownerId, summary)
  })
  return Array.from(summaries.values()).map(summary => ({...summary,
    required: roundMoney(summary.required), distributed: roundMoney(summary.distributed),
    remaining: roundMoney(Math.max(0, summary.required - summary.distributed)),
  }))
}

// Groups come from the existing withdrawal order, filtered to eligible, withdrawable IRAs.
// QCD credits come from the year's conversions unless a settlement pass supplies the amounts actually made.
// Rider contracts only ever pay their own RMD: carriers step up a paying rider to meet it (the
// SME-approved assumption), and any withdrawal during deferral reduces the rider's roll-up.
export function applyIraRmds(year: IFutureYear, groups: IAssetDetailGroup[], conversions: AssetConversion[] = year.conversions) {
  iraRmdSummaries(year.assetDetails, conversions).forEach(summary => {
    const owned = year.assetDetails.filter(ad => isAggregatableIra(ad.liquidAsset) && ad.liquidAsset.ownerId === summary.ownerId)
    if (year.yearNum === 1 && year.proRateBudget) {
      // Keep historical RMD cash in the annual budget without withdrawing it again.
      const nonCashCredits = owned.reduce((total, ad) => total + (ad.riderIncome ?? 0) + qcdCredit(ad, conversions), 0)
      let paid = Math.max(0, summary.required - nonCashCredits)
      owned.forEach(ad => {
        const amount = Math.min(paid, Math.max(0, -ad.withdrawals))
        ad.rmd += amount
        ad.distribution += amount
        ad.withdrawals += amount
        year.rmds += amount
        paid -= amount
      })
    }
    let remaining = summary.remaining
    const payOwnShortfall = (ad: IAssetDetail) => {
      const amount = roundMoney(Math.min(ownShortfall(ad, conversions), remaining, ad.balance))
      if (amount > 0) {
        distributeRmd(year, ad, amount)
        remaining = roundMoney(remaining - amount)
      }
    }
    owned.filter(ad => ad.riderActive && ad.balance > 0).forEach(payOwnShortfall)
    groups.forEach(group => {
      const details = group.details.filter(ad => ad.liquidAsset.ownerId === summary.ownerId && ad.balance > 0 && !ad.riderDeferred)
      let balance = details.reduce((total, ad) => total + ad.balance, 0)
      const withdrawal = Math.min(remaining, balance)
      let allocated = 0
      details.forEach((ad, index) => {
        const amount = roundMoney(Math.min(ad.balance, Math.max(0, withdrawal - allocated), Math.max(0, index === details.length - 1 ? withdrawal - allocated :
          roundMoney((withdrawal - allocated) * ad.balance / balance))))
        balance -= ad.balance
        distributeRmd(year, ad, amount)
        allocated += amount
      })
      remaining = roundMoney(Math.max(0, remaining - allocated))
    })
    owned.filter(ad => ad.riderDeferred && ad.balance > 0).forEach(payOwnShortfall)
  })
}
